import { createFilterOptions, Stack, styled, Tooltip, Typography, useTheme } from '@mui/material'
import { useState } from 'react'
import { assembleNameWithEmailFallback, assembleUserSearchText, DisplayUserLink, tokens } from 'ComponentLibrary'
import { OrderFilterContainer } from '../sharedStyles'
import { Checkbox } from 'ComponentLibrary/src/piazza/selectors'
import { compact, isEmpty } from 'lodash'
import { Dropdown } from 'ComponentLibrary/src/atoms/Dropdown/Dropdown'

type OrderAssignedUserFilterProps<T> = {
    label: string
    users: T[]
    setAssignedUsers: (assignedUser: number[]) => void
    filteredUsers: number[]
    isDisabled?: boolean
    disabledTooltip?: string
    loadingText: string
    emptyText: string
}

export function OrderUserFilter<T extends { id: number; email: string }>({
    label,
    users,
    setAssignedUsers,
    filteredUsers,
    isDisabled,
    disabledTooltip = '',
    loadingText,
    emptyText,
}: OrderAssignedUserFilterProps<T>) {
    const theme = useTheme()
    const userFilterOptions = createFilterOptions<T>({
        stringify: assembleUserSearchText,
    })

    const [autocompleteFocused, setAutocompleteFocused] = useState(false)

    return (
        <Tooltip title={isDisabled ? disabledTooltip : ''} arrow>
            <OrderFilterContainer>
                <Dropdown
                    label={label}
                    disabled={isDisabled}
                    open={autocompleteFocused}
                    value={compact(filteredUsers.map((userId) => users.find((user) => user.id === userId)))}
                    size="small"
                    multiple
                    disableCloseOnSelect
                    fullWidth
                    limitTags={1}
                    options={users}
                    filterOptions={userFilterOptions}
                    renderOption={(props, option, { selected }) => {
                        return (
                            <li {...props} key={option.id}>
                                <Stack direction="row" gap={tokens.SizingSpacing100}>
                                    <Checkbox checked={selected} />
                                    <DisplayUserLink user={option} size="small" showProfileOnHover={false} />
                                </Stack>
                            </li>
                        )
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onClick={() => setAutocompleteFocused(true)}
                    onFocus={() => setAutocompleteFocused(true)}
                    onBlur={() => {
                        setAutocompleteFocused(false)
                    }}
                    noOptionsText={isEmpty(users) ? loadingText : emptyText}
                    renderTags={(value) => {
                        const names = value.map((option) => {
                            return assembleNameWithEmailFallback(option)
                        })
                        return (
                            <StyledTag variant="bodyTextLarge" color={theme.palette.customtext.black100}>
                                {names.join(', ')}
                            </StyledTag>
                        )
                    }}
                    getOptionLabel={(option) => {
                        return assembleNameWithEmailFallback(option)
                    }}
                    onChange={(_, value) => {
                        setAssignedUsers(value.map((option) => option.id))
                    }}
                />
            </OrderFilterContainer>
        </Tooltip>
    )
}

const StyledTag = styled(Typography)`
    padding-left: 10px;
    margin-top: 3px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    position: absolute;
    max-width: 114px;
`
